import { useMutation } from "@apollo/client/react";
import { ColorInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { graphql } from "gql.tada";
import * as yup from "yup";
import { CreationForm } from "../../../components/form/CreationForm";

const UpdatePlatformThemeMutation = graphql(`
  mutation UpdatePlatformTheme($platformId: ID!, $input: PlatformThemeInput!) {
    platformThemeUpdate(platformId: $platformId, input: $input) {
      id
    }
  }
`);

const FONT_OPTIONS = [
  { label: "Inter", value: "Inter" },
  { label: "DM Sans", value: "DM Sans" },
  { label: "Rajdhani", value: "Rajdhani" },
  { label: "Plus Jakarta Sans", value: "Plus Jakarta Sans" },
  { label: "Space Grotesk", value: "Space Grotesk" },
  { label: "Poppins", value: "Poppins" },
];

const BORDER_RADIUS_OPTIONS = [
  { label: "0px (Sharp)", value: "0px" },
  { label: "4px (Subtle)", value: "4px" },
  { label: "8px (Rounded)", value: "8px" },
  { label: "12px (Smooth)", value: "12px" },
  { label: "16px (Pill)", value: "16px" },
];

interface ThemeEditorProps {
  platformId: string;
  initialTheme?: Record<string, string>;
}

export const ThemeEditor = ({ platformId, initialTheme }: ThemeEditorProps) => {
  const [updateTheme] = useMutation<any>(UpdatePlatformThemeMutation);

  return (
    <CreationForm
      title="Platform Theme"
      subtitle="Customize the visual appearance of your platform"
      submitText="Save Theme"
      schemaDefinition={{
        primaryColor: {
          type: "DYNAMIC",
          label: "Primary Color",
          yupConfig: yup.string().required("Primary color is required"),
          defaultValue: initialTheme?.primaryColor ?? "#6C5CE7",
          element: (onChange) => (
            <ColorInput
              label="Primary Color"
              description="Main brand color used for buttons and links"
              format="hex"
              swatches={[
                "#6C5CE7",
                "#0984E3",
                "#00B894",
                "#E17055",
                "#D63031",
                "#E84393",
                "#FDCB6E",
                "#2D3436",
              ]}
              defaultValue={initialTheme?.primaryColor ?? "#6C5CE7"}
              onChange={(value) => onChange(value)}
            />
          ),
        },
        secondaryColor: {
          type: "DYNAMIC",
          label: "Secondary Color",
          yupConfig: yup.string().required("Secondary color is required"),
          defaultValue: initialTheme?.secondaryColor ?? "#A29BFE",
          element: (onChange) => (
            <ColorInput
              label="Secondary Color"
              description="Accent color for highlights and secondary actions"
              format="hex"
              swatches={[
                "#A29BFE",
                "#74B9FF",
                "#55EFC4",
                "#FAB1A0",
                "#FF7675",
                "#FD79A8",
                "#FFEAA7",
                "#636E72",
              ]}
              defaultValue={initialTheme?.secondaryColor ?? "#A29BFE"}
              onChange={(value) => onChange(value)}
            />
          ),
        },
        backgroundColor: {
          type: "DYNAMIC",
          label: "Background Color",
          yupConfig: yup.string().required("Background color is required"),
          defaultValue: initialTheme?.backgroundColor ?? "#0F0F0F",
          element: (onChange) => (
            <ColorInput
              label="Background Color"
              description="Main page background color"
              format="hex"
              swatches={[
                "#0F0F0F",
                "#1A1A2E",
                "#16213E",
                "#0D1117",
                "#FFFFFF",
                "#F8F9FA",
                "#F1F3F5",
                "#E9ECEF",
              ]}
              defaultValue={initialTheme?.backgroundColor ?? "#0F0F0F"}
              onChange={(value) => onChange(value)}
            />
          ),
        },
        surfaceColor: {
          type: "DYNAMIC",
          label: "Surface Color",
          yupConfig: yup.string().required("Surface color is required"),
          defaultValue: initialTheme?.surfaceColor ?? "#1A1A1A",
          element: (onChange) => (
            <ColorInput
              label="Surface Color"
              description="Color for cards, modals, and elevated surfaces"
              format="hex"
              swatches={[
                "#1A1A1A",
                "#2D2D44",
                "#1E2A3A",
                "#161B22",
                "#FFFFFF",
                "#F8F9FA",
                "#E9ECEF",
                "#DEE2E6",
              ]}
              defaultValue={initialTheme?.surfaceColor ?? "#1A1A1A"}
              onChange={(value) => onChange(value)}
            />
          ),
        },
        textColor: {
          type: "DYNAMIC",
          label: "Text Color",
          yupConfig: yup.string().required("Text color is required"),
          defaultValue: initialTheme?.textColor ?? "#FFFFFF",
          element: (onChange) => (
            <ColorInput
              label="Text Color"
              description="Primary text color"
              format="hex"
              swatches={[
                "#FFFFFF",
                "#F8F9FA",
                "#E9ECEF",
                "#CED4DA",
                "#212529",
                "#343A40",
                "#495057",
                "#868E96",
              ]}
              defaultValue={initialTheme?.textColor ?? "#FFFFFF"}
              onChange={(value) => onChange(value)}
            />
          ),
        },
        textMutedColor: {
          type: "DYNAMIC",
          label: "Muted Text Color",
          yupConfig: yup.string().required("Muted text color is required"),
          defaultValue: initialTheme?.textMutedColor ?? "#888888",
          element: (onChange) => (
            <ColorInput
              label="Muted Text Color"
              description="Color for secondary and placeholder text"
              format="hex"
              swatches={[
                "#888888",
                "#6C757D",
                "#ADB5BD",
                "#868E96",
                "#495057",
                "#343A40",
                "#C0C0C0",
                "#A0A0A0",
              ]}
              defaultValue={initialTheme?.textMutedColor ?? "#888888"}
              onChange={(value) => onChange(value)}
            />
          ),
        },
        fontFamily: {
          type: "SELECT",
          label: "Body Font",
          yupConfig: yup.string().required("Body font is required"),
          options: FONT_OPTIONS,
          defaultValue: initialTheme?.fontFamily ?? "Inter",
          searchable: false,
        },
        headingFont: {
          type: "SELECT",
          label: "Heading Font",
          yupConfig: yup.string().required("Heading font is required"),
          options: FONT_OPTIONS,
          defaultValue: initialTheme?.headingFont ?? "Inter",
          searchable: false,
        },
        borderRadius: {
          type: "SELECT",
          label: "Border Radius",
          yupConfig: yup.string().required("Border radius is required"),
          options: BORDER_RADIUS_OPTIONS,
          defaultValue: initialTheme?.borderRadius ?? "8px",
          searchable: false,
        },
        logoUrl: {
          type: "INPUT",
          label: "Logo URL",
          yupConfig: yup.string().url("Must be a valid URL").nullable(),
          inputType: "url",
          placeholder: "https://example.com/logo.png",
          defaultValue: initialTheme?.logoUrl ?? "",
          info: "URL to your platform logo image (PNG or SVG recommended)",
        },
        twitter: {
          type: "INPUT",
          label: "Twitter / X",
          yupConfig: yup.string().url("Must be a valid URL").nullable(),
          inputType: "url",
          placeholder: "https://x.com/yourplatform",
          defaultValue: initialTheme?.twitter ?? "",
        },
        discord: {
          type: "INPUT",
          label: "Discord",
          yupConfig: yup.string().url("Must be a valid URL").nullable(),
          inputType: "url",
          placeholder: "https://discord.gg/yourserver",
          defaultValue: initialTheme?.discord ?? "",
        },
        twitch: {
          type: "INPUT",
          label: "Twitch",
          yupConfig: yup.string().url("Must be a valid URL").nullable(),
          inputType: "url",
          placeholder: "https://twitch.tv/yourchannel",
          defaultValue: initialTheme?.twitch ?? "",
        },
      }}
      onSubmit={async (values) => {
        try {
          await updateTheme({
            variables: {
              platformId,
              input: {
                primaryColor: values.primaryColor,
                secondaryColor: values.secondaryColor,
                backgroundColor: values.backgroundColor,
                surfaceColor: values.surfaceColor,
                textColor: values.textColor,
                textMutedColor: values.textMutedColor,
                fontFamily: values.fontFamily,
                headingFont: values.headingFont,
                borderRadius: values.borderRadius,
                logoUrl: values.logoUrl || null,
                socialLinks: {
                  twitter: values.twitter || null,
                  discord: values.discord || null,
                  twitch: values.twitch || null,
                },
              },
            },
          }).then((data) => {
            if (data?.error) {
              throw new Error(data.error.message);
            }
          });

          notifications.show({
            title: "Theme updated",
            message: "Your platform theme has been saved successfully",
            color: "green",
            autoClose: 3000,
          });
        } catch (error) {
          notifications.show({
            title: "Failed to update theme",
            message:
              error instanceof Error
                ? error.message
                : "An unexpected error occurred",
            color: "red",
            autoClose: 5000,
          });
        }
      }}
    />
  );
};
